/**
 * Admin → Profile Cache page (v1.3.36).
 *
 * Shows every per-playlist snapshot of profiles + favorites + watch
 * history + app settings that devices have pushed to the server. The
 * admin can inspect a single entry in a dialog (raw JSON) or delete it
 * to force the device to start fresh on next launch.
 */
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { adminAxios } from "../lib/api";
import { Users, RefreshCw, Trash2, Eye, Database } from "lucide-react";

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}

export default function ProfileCache() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [viewing, setViewing] = useState(null); // {playlist_id, playlist_name, payload}

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminAxios.get("/profile-cache");
      setItems(r.data?.items || []);
    } catch (e) {
      toast({ title: "Failed to load profile cache", description: e?.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const openView = async (row) => {
    try {
      const r = await adminAxios.get(`/profile-cache/${encodeURIComponent(row.playlist_id)}`);
      setViewing({ ...row, payload: r.data });
    } catch (e) {
      toast({ title: "Could not load cache", description: e?.response?.data?.detail || e?.message, variant: "destructive" });
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(
      `Delete the cached profiles, favorites, history and app settings for "${row.playlist_name}"?\n\n`
      + `This wipes the server copy only — devices that have a local copy can re-upload it the next time they save anything. `
      + `If a device re-installs after this, the user starts from scratch.`
    )) return;
    setDeleting(row.playlist_id);
    try {
      await adminAxios.delete(`/profile-cache/${encodeURIComponent(row.playlist_id)}`);
      toast({ title: "Cache deleted", description: row.playlist_name });
      setItems((prev) => prev.filter((x) => x.playlist_id !== row.playlist_id));
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || e?.message, variant: "destructive" });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="w-6 h-6 text-orange-500" />
            Profile Cache
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-3xl">
            Server-side snapshot of every device&apos;s profiles, favorites, watch history and player settings — one entry per playlist.
            Devices push automatically whenever the user adds a favorite, finishes an episode or changes a setting. Use this page to audit or wipe a client&apos;s saved state.
          </p>
        </div>
        <Button
          data-testid="admin-profile-cache-refresh"
          variant="outline"
          onClick={load}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="admin-profile-cache-table">
            <TableHeader>
              <TableRow>
                <TableHead>Playlist</TableHead>
                <TableHead className="text-right">Profiles</TableHead>
                <TableHead className="text-right">Favorites</TableHead>
                <TableHead className="text-right">History</TableHead>
                <TableHead>Last device</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-slate-400">
                    <Users className="w-8 h-8 mx-auto mb-3 opacity-50" />
                    No profile caches yet. Devices will push their snapshot the next time they save a favorite, finish an episode or change a player setting.
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.map((row) => (
                <TableRow key={row.playlist_id} data-testid={`admin-profile-cache-row-${row.playlist_id}`}>
                  <TableCell className="font-medium">
                    {row.playlist_name}
                    <div className="text-[11px] text-slate-500 mt-0.5 font-mono truncate max-w-[260px]">{row.playlist_id}</div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.profile_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.favorites_total}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.history_total}</TableCell>
                  <TableCell className="text-xs font-mono text-slate-400 truncate max-w-[160px]">{row.device_id || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-300">{fmt(row.updated_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        data-testid={`admin-profile-cache-view-${row.playlist_id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => openView(row)}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" /> View
                      </Button>
                      <Button
                        data-testid={`admin-profile-cache-delete-${row.playlist_id}`}
                        size="sm"
                        variant="destructive"
                        disabled={deleting === row.playlist_id}
                        onClick={() => handleDelete(row)}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        {deleting === row.playlist_id ? "Deleting…" : "Delete"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{viewing?.playlist_name} — cached profile data</DialogTitle>
            <DialogDescription>
              Raw JSON pulled from the server. Read-only — to wipe this entry use the Delete button on the row.
            </DialogDescription>
          </DialogHeader>
          {viewing && (
            <pre
              data-testid="admin-profile-cache-view-json"
              className="text-[11px] bg-slate-950 border border-white/5 rounded-md p-3 max-h-[60vh] overflow-auto whitespace-pre-wrap break-all text-slate-300"
            >
              {JSON.stringify(viewing.payload, null, 2)}
            </pre>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
